import { StackNavigationProp, StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import React, { useCallback, useLayoutEffect } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useI18n } from "../../hooks/i18n-hooks";
import { useNavigation } from "@react-navigation/native";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import VStack from "../../ui_atoms/VStack";
import { useApolloErrorSnackbar, useSnackbar } from "../../hooks/snackbar";
import TextField from "../../components/TextField";
import MarkdownView from "../../components/MarkdownView";
import { CANCEL_AMENDMENT, GET_AMENDMENT, GET_MISSION_AMENDMENTS, REJECT_AMENDMENT } from "../../graphql/amendment";
import ScreenCard from "../ScreenCard";
import Loading from "../../components/Loading";
import { GET_MISSION_WITH_TERMINATION } from "../../graphql/core";

type FormData = {
    reason: string;
};

type AmendmentCancellationProps = StackScreenProps<RootStackParamList, "AmendmentCancellation">;

const AmendmentCancellationScreen = ({ route, navigation: stackNavigation }: AmendmentCancellationProps) => {
    const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
    const i18n = useI18n();
    const { amendmentId, mode } = route?.params || {};

    const isCancel = mode === "cancel";
    const i18nPrefix = isCancel ? "screens.cancel_amendment" : "screens.reject_amendment";

    useLayoutEffect(() => {
        stackNavigation.setOptions({ title: i18n.t(`${i18nPrefix}.title`) });
    }, [stackNavigation, i18n, i18nPrefix]);

    const {
        data: getAmendmentData,
        error: getAmendmentError,
        loading: getAmendmentLoading,
    } = useQuery(GET_AMENDMENT, {
        variables: { id: amendmentId },
        fetchPolicy: "cache-and-network",
    });

    const [rejectAmendment, { error: rejectAmendmentError, loading: rejectAmendmentLoading }] =
        useMutation(REJECT_AMENDMENT);

    const [cancelAmendment, { error: cancelAmendmentError, loading: cancelAmendmentLoading }] =
        useMutation(CANCEL_AMENDMENT);

    const error = getAmendmentError || rejectAmendmentError || cancelAmendmentError;

    const { showSnackbar } = useSnackbar();

    useApolloErrorSnackbar(error);

    const {
        control,
        handleSubmit,
        formState: { isValid },
    } = useForm<FormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
    });

    const onSubmit = useCallback<SubmitHandler<FormData>>(
        async (formData) => {
            const missionId = getAmendmentData?.getAmendment?.missionId || "";

            if (isCancel) {
                const result = await cancelAmendment({
                    variables: { amendmentId, reason: formData.reason },
                    refetchQueries: [GET_MISSION_WITH_TERMINATION, GET_MISSION_AMENDMENTS],
                });
                if (result.data?.cancelAmendment) {
                    navigation.navigate("MissionDetails", { missionId });
                    showSnackbar({ mode: "success", text: i18n.t("screens.cancel_amendment.success") });
                }
            } else {
                const result = await rejectAmendment({
                    variables: { amendmentId, reason: formData.reason },
                });
                if (result.data?.rejectAmendment) {
                    navigation.navigate("MissionDetails", { missionId });
                    showSnackbar({ mode: "success", text: i18n.t("screens.reject_amendment.success") });
                }
            }
        },
        [
            isCancel,
            cancelAmendment,
            rejectAmendment,
            amendmentId,
            navigation,
            getAmendmentData?.getAmendment?.missionId,
            showSnackbar,
            i18n,
        ],
    );

    return !getAmendmentLoading ? (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t(`${i18nPrefix}.submit`),
                    mode: "danger",
                    size: "medium",
                    isValid,
                    validating: rejectAmendmentLoading || cancelAmendmentLoading,
                    onPress: handleSubmit(onSubmit),
                },
            ]}
        >
            <VStack gap={1} h={1}>
                <MarkdownView markdown={i18n.t(`${i18nPrefix}.description`)} />
                <Controller
                    name="reason"
                    control={control}
                    rules={{
                        required: i18n.t(`${i18nPrefix}.missing_other_reason`),
                    }}
                    render={({ field: { onChange, value, onBlur }, fieldState: { error: fieldError } }) => {
                        return (
                            <TextField
                                multiline
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                label={i18n.t(`${i18nPrefix}.reason_label`)}
                                error={fieldError?.message}
                            />
                        );
                    }}
                />
            </VStack>
        </ScreenCard>
    ) : (
        <Loading />
    );
};

export default AmendmentCancellationScreen;
